import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from '../../AddPrimaryContact/AddPrimaryContact.module.scss';
import LDAPSearchField from '../../shared/LDAPSearch/LDAPSearchField';

interface Contact {
  role: string;
  name: string;
  imageUrl?: string;
  type?: string;
  uid?: string;
  email?: string;
  department?: string;
  id?: string;
  isExisting?: boolean;
}

interface EditPrimaryContactProps {
  isOpen: boolean;
  contact: Contact | null; // The contact being edited (for context, but we show all roles)
  existingContacts?: Contact[]; // All existing primary contacts across all roles
  onClose: () => void;
  onSave: (role: string, selectedContacts: Contact[]) => void;
  lastUpdated?: string; // e.g., "January 22, 2025 12:37PM"
  updatedBy?: string; // e.g., "Dima Chekhter"
  title?: string; // Optional title override (default: "Edit Primary Contact")
}

/**
 * Pixel-perfect Edit Primary Contact modal matching Figma design.
 * Shows all 3 roles with existing contacts as chips.
 * Design: https://www.figma.com/design/hBnevR1MeKGrm062iaGE1P/Roadmap?node-id=2035-332395
 */
const EditPrimaryContact: React.FC<EditPrimaryContactProps> = ({
  isOpen,
  contact,
  existingContacts = [],
  onClose,
  onSave,
  lastUpdated,
  updatedBy,
  title = 'Edit' // Default to Edit mode
}) => {
  // State for each role's contacts
  const [pmmContacts, setPmmContacts] = useState<Contact[]>([]);
  const [pgmContacts, setPgmContacts] = useState<Contact[]>([]);
  const [pmContacts, setPmContacts] = useState<Contact[]>([]);
  // Error state for validation
  const [validationError, setValidationError] = useState<string>('');

  // Role definitions
  const roles = [
    {
      id: 'pmm',
      name: 'PROD MRKT MANAGER',
      label: 'PRODUCT MARKETING MANAGER',
      state: pmmContacts,
      setState: setPmmContacts
    },
    {
      id: 'pgm',
      name: 'PROGRAM MANAGER',
      label: 'PROGRAM MANAGER',
      state: pgmContacts,
      setState: setPgmContacts
    },
    {
      id: 'pm',
      name: 'PRODUCT MANAGER',
      label: 'PRODUCT MANAGER',
      state: pmContacts,
      setState: setPmContacts
    }
  ];

  // Initialize all roles with existing contacts on modal open
  useEffect(() => {
    if (isOpen && existingContacts) {
      // Filter and mark existing contacts for each role
      // Keep all properties including ID (crucial for deletion detection)
      const pmm = existingContacts
        .filter(c => c.role === 'PROD MRKT MANAGER')
        .map(c => ({ ...c, isExisting: true }));

      const pgm = existingContacts
        .filter(c => c.role === 'PROGRAM MANAGER')
        .map(c => ({ ...c, isExisting: true }));

      const pm = existingContacts
        .filter(c => c.role === 'PRODUCT MANAGER')
        .map(c => ({ ...c, isExisting: true }));

      setPmmContacts(pmm);
      setPgmContacts(pgm);
      setPmContacts(pm);
    }
  }, [isOpen, existingContacts]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setPmmContacts([]);
      setPgmContacts([]);
      setPmContacts([]);
      setValidationError(''); // Clear error on close
    }
  }, [isOpen]);

  const handleSave = () => {
    // VALIDATION: Check if at least one contact exists across all roles
    const totalContacts =
      pmmContacts.length + pgmContacts.length + pmContacts.length;

    if (totalContacts === 0) {
      setValidationError(
        'At least one primary contact is required. Please add a contact before saving.'
      );
      return;
    }

    // Clear any existing error
    setValidationError('');

    // Get original contacts for each role to detect changes
    const originalPmm = existingContacts.filter(
      c => c.role === 'PROD MRKT MANAGER'
    );
    const originalPgm = existingContacts.filter(
      c => c.role === 'PROGRAM MANAGER'
    );
    const originalPm = existingContacts.filter(
      c => c.role === 'PRODUCT MANAGER'
    );

    // Helper to check if a role has changes (additions or deletions)
    const hasChanges = (current: Contact[], original: Contact[]) => {
      // Check if counts differ (additions or deletions)
      if (current.length !== original.length) return true;

      // Check if any contact UIDs differ
      const currentUids = new Set(current.map(c => c.uid || c.name));
      const originalUids = new Set(original.map(c => c.uid || c.name));

      // Check for additions
      const currentUidsArray = Array.from(currentUids);
      for (let i = 0; i < currentUidsArray.length; i++) {
        if (!originalUids.has(currentUidsArray[i])) return true;
      }

      // Check for deletions
      const originalUidsArray = Array.from(originalUids);
      for (let i = 0; i < originalUidsArray.length; i++) {
        if (!currentUids.has(originalUidsArray[i])) return true;
      }

      return false;
    };

    // Save each role that has changes (additions OR deletions)
    // Send ALL current contacts (not just new ones) so the backend can detect deletions
    if (hasChanges(pmmContacts, originalPmm)) {
      onSave('PROD MRKT MANAGER', pmmContacts);
    }

    if (hasChanges(pgmContacts, originalPgm)) {
      onSave('PROGRAM MANAGER', pgmContacts);
    }

    if (hasChanges(pmContacts, originalPm)) {
      onSave('PRODUCT MANAGER', pmContacts);
    }

    onClose();
  };

  if (!isOpen) return null;

  // Render to document.body to ensure overlay covers entire viewport in full browser mode
  // This matches the behavior of DACI modal, decisions modal, and edit milestone modal
  return createPortal(
    <div className={styles.overlay} data-testid="edit-primary-contact-overlay">
      <div
        className={styles.modalContainer}
        data-testid="edit-primary-contact-modal"
      >
        <div
          className={styles.addPrimaryContactContainer}
          data-testid="edit-primary-contact-container"
        >
          {/* Header - Figma: Frame 26086666 */}
          <div
            className={styles.header}
            data-testid="edit-primary-contact-header"
          >
            <div className={styles.headerText}>
              <h2 className={styles.title}>{title}</h2>
              <div className={styles.subtitle}>
                Minimum 1 Primary Contact is Required
              </div>
            </div>
          </div>

          {/* Form - Figma: Frame 26086675 */}
          <div
            className={styles.formContainer}
            data-testid="edit-primary-contact-form"
            style={{ gap: '20px' }} // Figma: gap 20px between role sections
          >
            {roles.map(role => {
              // Check if this role matches the contact being edited
              const isCurrentRole = contact?.role === role.name;

              return (
                <div
                  key={role.id}
                  className={`${styles.formGroup} ${
                    validationError ? styles.hasError : ''
                  }`}
                  data-testid={`edit-primary-contact-${role.id}-group`}
                  style={{
                    gap: '6px' // Figma: gap 6px between label and field
                  }}
                >
                  <label
                    className={styles.label}
                    style={{
                      fontFamily: 'Adobe Clean',
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '1.3em',
                      color: '#6D6D6D'
                    }}
                  >
                    {role.label} *
                  </label>
                  <div
                    className={styles.ldapSearchContainer}
                    data-testid={`edit-primary-contact-${role.id}-ldap-container`}
                  >
                    <LDAPSearchField
                      key={`edit-ldap-${role.id}`}
                      label=""
                      value={role.state}
                      onChange={val => {
                        // LDAP field may return a single object or array; normalize here
                        if (Array.isArray(val)) {
                          role.setState(val as Contact[]);
                        } else {
                          role.setState(val ? [val] : []);
                        }
                      }}
                      isRequired={false}
                      existingContacts={role.state} // Pass current role's state to reflect deletions
                      currentDaciRole={role.name} // Enable role-specific duplicate checking
                      autoFocus={isCurrentRole} // Auto-focus the field being edited
                      cssClasses={{
                        selectedTag: styles.selectedTag,
                        tagContainer: styles.tagContainer,
                        ldapDropdown: styles.ldapDropdown,
                        ldapFieldContainer: styles.ldapFieldContainer
                      }}
                    />
                  </div>
                </div>
              );
            })}

            {/* Validation Error Message */}
            {validationError && (
              <div
                className={styles.errorMessage}
                data-testid="edit-primary-contact-error"
              >
                {validationError}
              </div>
            )}
          </div>

          {/* Footer with Last Updated info - Figma: Frame 314667 */}
          {(lastUpdated || updatedBy) && (
            <div
              className={styles.lastUpdatedFooter}
              data-testid="edit-primary-contact-footer"
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                marginTop: '20px'
              }}
            >
              {lastUpdated && (
                <span
                  style={{
                    fontFamily: 'Adobe Clean',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '1.5em',
                    color: '#6D6D6D'
                  }}
                >
                  Last Updated: {lastUpdated}
                </span>
              )}
              {updatedBy && (
                <span
                  style={{
                    fontFamily: 'Adobe Clean',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '1.5em',
                    color: '#6D6D6D'
                  }}
                >
                  By: {updatedBy}
                </span>
              )}
            </div>
          )}

          {/* Action Buttons - Figma: Frame 26086678 */}
          <div
            className={styles.actionsContainer}
            data-testid="edit-primary-contact-actions"
          >
            <button
              className={styles.cancelButton}
              onClick={onClose}
              aria-label="Cancel edit primary contact"
            >
              <span
                className={`${styles.buttonText} ${styles.cancelButtonText}`}
              >
                Cancel
              </span>
            </button>
            <button
              className={styles.saveButton}
              onClick={handleSave}
              aria-label="Save primary contact"
            >
              <span className={`${styles.buttonText} ${styles.saveButtonText}`}>
                Save
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body // Render to document.body for full viewport coverage
  );
};

export default EditPrimaryContact;
